import React, { useState, useEffect, useCallback } from "react";
import { Card, Row, Col, Button, Spinner } from "react-bootstrap";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Avatar from "components/common/Avatar";
import { useUserManagement } from "hooks";
import { useAuth } from "contexts/AuthContext";
import SubtleBadge from "components/common/SubtleBadge";
import useFormattedDate from "hooks/useFormatDate";
import PaymentInfo from "components/pages/user/profile/PaymentInfo";
import * as MaskData from "maskdata";
import placeholderPhoto from "assets/img/profileplaceholder.png";
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import UserAffiliationAssignmentsTable from "./UserAffiliationAssignmentsTable";
import StatusUpdateModal from "./StatusUpdateModal";
import { Popover } from "antd";
import UserAuditLogs from "components/pages/user/profile/UserAuditLogs";
import CopyableText from "components/common/CopyableText";
import paths from "routes/paths";
const UserDetailsPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sensitiveVisible, setSensitiveVisible] = useState({
    birthDate: false,
    ssn: false,
    taxId: false,
  });
  const [showStatusModal, setShowStatusModal] = useState(false);
  const { getUserById } = useUserManagement();
  const { user: loggedInUser } = useAuth();
  const loggedInUserId = loggedInUser?.id;

  // Helper masks for DOB and SSN
  const maskDOB = (dob) => {
    if (!dob) return "";
    // mask all except year (last 4 chars)
    return MaskData.maskStringV2(dob, {
      maskWith: "*",
      unmaskedStartCharacters: 0,
      unmaskedEndCharacters: 4, // show year only
    });
  };

  const maskSSN = (ssn) => {
    if (!ssn) return "";
    return MaskData.maskStringV2(ssn, {
      maskWith: "*",
      unmaskedStartDigits: 0,
      unmaskedEndDigits: 4,
    });
  };

  const maskTaxId = (taxId) => {
    if (!taxId) return "";
    return MaskData.maskStringV2(taxId, {
      maskWith: "*",
      unmaskedStartCharacters: 0,
      unmaskedEndCharacters: 4,
    });
  };

  const formatEnumValue = (value) => {
    if (!value) return "Not provided";
    return String(value)
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const formatBooleanValue = (value) => {
    if (typeof value !== "boolean") return "Not provided";
    return value ? "Yes" : "No";
  };

  const { formatDateTime } = useFormattedDate();

  const statusColors = {
    ACTIVE: "success",
    DISABLED: "secondary",
    SUSPENDED: "warning",
    DELETED: "danger",
  };

  // Format address object into a string
  const formatAddress = (addr) => {
    if (!addr) return "Not provided";
    const parts = [
      addr.street,
      addr.city,
      addr.state,
      addr.zipCode,
      addr.country,
    ].filter(Boolean);
    return parts.length ? parts.join(", ") : "Not provided";
  };

  const normalizeUserData = useCallback((data) => {
    if (!data) return null;
    return {
      id: data.id,
      username: data.username || null,
      firstName: data.firstName || null,
      middleName: data.middleName || null,
      lastName: data.lastName || null,
      gender: data.gender || null,
      ssn: data.ssn || null,
      taxIdType: data.taxIdType || null,
      taxId: data.taxId || null,
      email: data.email || data.contactDetails?.personalEmail || null,
      phone: data.phone || data.contactDetails?.mobilePhone || null,
      status: data.status || null,
      statusChangeNotes: data.statusChangeNotes || null,
      profilePhoto: data.profilePhoto || null,
      profilePhotoAt: data.profilePhotoAt || null,
      birthDate: data.birthDate || null,
      securityPhoto: data.securityPhoto || null,
      securityPhotoAt: data.securityPhotoAt || null,
      identificationDocument: data.identificationDocument || null,
      identificationDocumentType:
        data.identificationDocumentType ||
        data.userIdentityVerification?.identificationDocumentType ||
        null,
      contactDetails: data.contactDetails || {},
      permanentAddress: data.permanentAddress || null,
      residentialAddress: data.residentialAddress || null,
      workAddress: data.workAddress || null,
      schoolAddress: data.schoolAddress || null,
      billingAddress: data.billingAddress || null,
      preferredMailingAddress: data.preferredMailingAddress || "Not provided",
      preferredContactMethod: data.preferredContactMethod || "Not specified",
      userIdentityVerification: data.userIdentityVerification || null,
      userWorkAuthorization: data.userWorkAuthorization || null,
      helperProfile: data.helperProfile || null,
      helperProfileStatus:
        data.helperProfileStatus ||
        data.helperProfile?.helperStatus ||
        data.helperProfile?.status ||
        null,
      helperApprovedAt:
        data.helperApprovedAt || data.helperProfile?.approvedAt || null,
      helperRejectedAt:
        data.helperRejectedAt || data.helperProfile?.rejectedAt || null,
      helperCreatedAt:
        data.helperCreatedAt ||
        data.helperProfile?.createdAt ||
        data.helperProfile?.signedUpAt ||
        null,
      helperUpdatedAt:
        data.helperUpdatedAt ||
        data.helperProfile?.updatedAt ||
        data.helperProfile?.signedUpAt ||
        null,
      helperStatusChangeNotes:
        data.helperStatusChangeNotes ||
        data.helperProfile?.statusNotes ||
        data.helperProfile?.statusChangeNotes ||
        data.helperProfile?.notes ||
        null,
      verified: data.verified || false,
      idPhotoVerified: data.idPhotoVerified || false,
      uploadedOn: data.userIdentityVerification?.uploadedOn || null,
    };
  }, []);

  const toggleSensitive = (field) => {
    setSensitiveVisible((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;
      setLoading(true);
      setError(null);
      try {
        const data = await getUserById(userId);
        setUser(normalizeUserData(data));
      } catch (err) {
        setError(err.message || "Failed to load user");
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId, getUserById, normalizeUserData]);

  const getStatusNote = (status, note) => {
    if (["SUSPENDED", "DELETED"].includes(status)) {
      return note || "No notes provided";
    }

    if (status === "REGISTRATION") {
      return "Account is under registration";
    }

    if (status === "PENDING") {
      return "Account not approved";
    }

    return `Account is ${status?.toLowerCase()}`;
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "400px" }}
      >
        <Spinner animation="border" variant="success" />
      </div>
    );
  }

  if (error) {
    return <p className="text-center mt-5 text-danger">{error}</p>;
  }

  if (!user) {
    return <p className="text-center mt-5">User not found</p>;
  }

  const badgeColor = statusColors[user.status] || "secondary";
  const hasHelperProfile = Boolean(
    user.helperProfile || user.helperProfileStatus,
  );
  const helperStatusLabel = user.helperProfileStatus || "Not available";
  const helperBadgeColors = {
    APPROVED: "success",
    VERIFIED: "success",
    PENDING: "warning",
    SUSPENDED: "warning",
    CANCELLED: "danger",
    TERMINATED: "danger",
    DELETED: "danger",
    NONE: "secondary",
  };
  const helperBadgeColor =
    helperBadgeColors[user.helperProfileStatus] || "secondary";
  const isUsersTablePath = (path) =>
    typeof path === "string" &&
    (path.startsWith("/olitask/users/staff") ||
      path.startsWith("/olitask/users/customers"));
  const stateFromPath = location.state?.fromPath;
  const savedFromPath =
    typeof window === "undefined"
      ? null
      : window.sessionStorage.getItem("olitaskUsersListPath");
  const fallbackListPath =
    location.state?.from === "staff"
      ? "/olitask/users/staff"
      : "/olitask/users/customers";
  const backPath = isUsersTablePath(stateFromPath)
    ? stateFromPath
    : isUsersTablePath(savedFromPath)
    ? savedFromPath
    : fallbackListPath;
  const workAuth = user.userWorkAuthorization || {};
  const hasWorkAuthData = Object.keys(workAuth).length > 0;

  return (
    <>
      <Card className="mb-4 shadow-none mt-3 rounded border-1">
        <Card.Body className="d-flex flex-wrap align-items-center justify-content-between py-3 px-4">
          {/* Back button */}
          <div className="d-flex align-items-center mb-2 mb-sm-0">
            <Button
              type="button"
              onClick={() => navigate(backPath)}
              size="sm"
              aria-label="Go back"
              variant="outline-success"
            >
              <span style={{ fontSize: "1.3em", lineHeight: 1 }}>←</span> Back
            </Button>
          </div>

          {/* Title */}
          <h4 className="mb-2 mb-sm-0 fw-semibold text-base flex-grow-1 text-center ms-3 text-sm-start">
            User Details
          </h4>

          {/* Action buttons */}
          <div
            className="d-flex gap-2 flex-wrap justify-content-center justify-content-sm-end"
            style={{ minWidth: 180 }}
          >
            <Guard privilege={PRIV.UPDATE_USERS}>
              <Button
                variant="success"
                size="sm"
                onClick={() => navigate(`/olitask/users/${user.id}/edit`)}
                className="text-nowrap text-xs"
              >
                Edit User
              </Button>
            </Guard>

            <Guard privilege={PRIV.UPDATE_USERS}>
              <Button
                variant="primary"
                size="sm"
                onClick={() =>
                  navigate(`/olitask/users/${user.id}/actionables`)
                }
                className="text-nowrap text-xs"
              >
                Actionables
              </Button>
            </Guard>

            <Guard privilege={PRIV.VERIFY_USERS}>
              <Button
                variant="info"
                size="sm"
                onClick={() =>
                  navigate(`/olitask/users/${user.id}/identity-documents`)
                }
                className="text-nowrap text-xs"
              >
                Verify Documents
              </Button>
            </Guard>

            <Guard privilege={PRIV.VIEW_USERS}>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => navigate(paths.userHelperProfile(user.id))}
                disabled={!user.helperProfile && !user.helperProfileStatus}
                className="text-nowrap text-xs"
              >
                Helper Profile
              </Button>
              <Button
                variant="outline-info"
                size="sm"
                onClick={() =>
                  navigate(
                    `${paths.subscriptions}?tab=subscriptions&subscriberType=USER&subscriberId=${encodeURIComponent(
                      user.id,
                    )}`,
                  )
                }
                className="text-nowrap text-xs"
              >
                Subscriptions
              </Button>
            </Guard>
          </div>
        </Card.Body>
      </Card>

      <Row className="g-3">
        <Col lg={4} className="mb-4">
          <Card className="pb-4 custom-shadow">
            <Card.Header>
              <h6 className="mb-0 fw-bold text-center">Profile Overview</h6>
            </Card.Header>

            <Card.Body className="py-0 text-center">
              <div className="mb-2">
                <Avatar
                  src={user.profilePhoto || placeholderPhoto}
                  size="5xl"
                  className="mb-2"
                  onError={(e) => (e.currentTarget.src = placeholderPhoto)}
                />
              </div>

              <div className="mb-2 mt-2">
                <h6>Security Photo</h6>
                <Avatar
                  src={user.securityPhoto || placeholderPhoto}
                  size="5xl"
                  onError={(e) => (e.currentTarget.src = placeholderPhoto)}
                />
              </div>

              <div className="d-flex justify-content-center gap-2 mb-3">
                <SubtleBadge pill bg={badgeColor}>
                  {user.status}
                </SubtleBadge>
              </div>
            </Card.Body>
          </Card>

          <Card className="custom-shadow mt-3">
            <h6 className="mt-3 px-3 fw-bold">Helper Profile</h6>
            <div className="px-3 mt-2 pb-3">
              <div className="mb-2">
                <label className="form-label text-xxs mb-0 d-block">
                  Current Helper Status
                </label>
                <SubtleBadge pill bg={helperBadgeColor}>
                  {helperStatusLabel}
                </SubtleBadge>
              </div>

              <div className="mb-2">
                <label className="form-label text-xxs mb-0 d-block">
                  Helper Profile Created
                </label>
                <h6 className="mb-0 fw-semibold text-sm">
                  {formatDateTime(user.helperCreatedAt)}
                </h6>
              </div>

              <div className="mb-3">
                <label className="form-label text-xxs mb-0 d-block">
                  Status Notes
                </label>
                <h6 className="mb-0 fw-semibold text-sm">
                  {user.helperStatusChangeNotes || "Not provided"}
                </h6>
              </div>

              <Button
                variant="success"
                size="sm"
                className="w-100"
                onClick={() => navigate(paths.userHelperProfile(user.id))}
                disabled={!hasHelperProfile}
              >
                View more details
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                className="w-100 mt-2"
                onClick={() => navigate(`${paths.userHelperProfile(user.id)}#profession-profiles`)}
                disabled={!hasHelperProfile}
              >
                Profession Profiles
              </Button>
              {!hasHelperProfile && (
                <small className="text-muted d-block mt-2">
                  This user does not have a helper profile yet.
                </small>
              )}
            </div>
          </Card>
        </Col>

        <Col lg={8}>
          <Row className="g-3">
            {/* Personal Information */}
            <Col md={6}>
              <Card className="custom-shadow">
                <h6 className="mt-3 px-3 fw-bold">Personal Information</h6>
                <div className="px-3 mt-2">
                  <div className="mb-2">
                    <label className="form-label text-xxs mb-0">User ID</label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      <CopyableText value={user.id} />
                    </h6>
                  </div>
                  <div className="mb-2">
                    <label className="form-label text-xxs mb-0">
                      Full Name
                    </label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {`${user.firstName || ""} ${user.middleName || ""} ${
                        user.lastName || ""
                      }`.trim() || "Not provided"}
                    </h6>
                  </div>

                  <div className="mb-2">
                    <label className="form-label text-xxs mb-0">Username</label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {user.username || "Not provided"}
                    </h6>
                  </div>

                  <div className="mb-2">
                    <label className="form-label text-xxs mb-0">Email</label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {user.email || "Not provided"}
                    </h6>
                  </div>

                  <div className="mb-2">
                    <label className="form-label text-xxs mb-0">Phone</label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {user.phone || "Not provided"}
                    </h6>
                  </div>

                  <div className="mb-2">
                    <label className="form-label text-xxs mb-0">
                      Mobile Phone
                    </label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {user.mobilePhone || "Not provided"}
                    </h6>
                  </div>

                  <div className="mb-2">
                    <label className="form-label text-xxs mb-0">
                      Work Phone
                    </label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {user.workPhone || "Not provided"}
                    </h6>
                  </div>

                  <div className="mb-2">
                    <label className="form-label text-xxs mb-0">
                      Home Phone
                    </label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {user.homePhone || "Not provided"}
                    </h6>
                  </div>

                  <div className="mb-2">
                    <div className="d-flex align-items-center justify-content-between">
                      <label className="form-label text-xxs mb-0">
                        Date of Birth (YYYY-MM-DD)
                      </label>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-secondary"
                        onClick={() => toggleSensitive("birthDate")}
                        disabled={!user.birthDate}
                        style={{ textDecoration: "none", fontSize: "0.75rem" }}
                      >
                        {sensitiveVisible.birthDate ? "Hide" : "Show"}
                      </Button>
                    </div>

                    {/* Birth Date */}
                    <h6 className="mb-0 fw-semibold text-sm">
                      {user.birthDate
                        ? sensitiveVisible.birthDate
                          ? user.birthDate
                          : maskDOB(user.birthDate) // masked DOB shown when hidden
                        : "Not provided"}
                    </h6>
                  </div>

                  <div className="mb-2">
                    <label className="form-label text-xxs mb-0">Gender</label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {user.gender || "Not provided"}
                    </h6>
                  </div>
                </div>
              </Card>
            </Col>

            {/* Additional Info */}
            <Col md={6}>
              <Card className="custom-shadow">
                <h6 className="mt-3 px-3 fw-bold">Additional Information</h6>
                <div className="px-3 mt-1">
                  {" "}
                  <div className="mb-2">
                    <label className="form-label text-xxs mb-0 d-block">
                      Tax ID Type
                    </label>
                    <h6 className="mb-0 fw-semibold text-xs">
                      {user.taxIdType || "Not provided"}
                    </h6>
                  </div>
                  <div className="mb-2">
                    <div className="d-flex align-items-center justify-content-between">
                      <label className="form-label text-xxs mb-0 d-block">
                        Tax ID
                      </label>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-secondary"
                        onClick={() => toggleSensitive("taxId")}
                        disabled={!user.taxId}
                        style={{ textDecoration: "none", fontSize: "0.75rem" }}
                      >
                        {sensitiveVisible.taxId ? "Hide" : "Show"}
                      </Button>
                    </div>
                    <h6 className="mb-0 fw-semibold text-xs">
                      {user.taxId
                        ? sensitiveVisible.taxId
                          ? user.taxId
                          : maskTaxId(user.taxId)
                        : "Not provided"}
                    </h6>
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-xxs mb-0 d-block">
                      Profile Photo Status
                    </label>
                    <SubtleBadge
                      pill
                      bg={user.profilePhoto ? "success" : "secondary"}
                    >
                      {user.profilePhoto ? "Uploaded" : "Not uploaded"}
                    </SubtleBadge>
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-xxs mb-0">
                      School Address
                    </label>
                    <h6 className="mb-0 fw-semibold text-xs">
                      {formatAddress(user.schoolAddress)}
                    </h6>
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-xxs mb-0">
                      Billing Address
                    </label>
                    <h6 className="mb-0 fw-semibold text-xs">
                      {formatAddress(user.billingAddress)}
                    </h6>
                  </div>
                  <div className="mb-2">
                    <label className="form-label text-xxs mb-0">
                      Work Email
                    </label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {user.workEmail || "Not provided"}
                    </h6>
                  </div>
                  <div className="mb-2">
                    <label className="form-label text-xxs mb-0">Website</label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {user.website || "Not provided"}
                    </h6>
                  </div>
                  <div className="mb-2">
                    <label className="form-label text-xxs mb-0">Twitter</label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {user.twitter || "Not provided"}
                    </h6>
                  </div>
                  <div className="mb-2">
                    <label className="form-label text-xxs mb-0">Facebook</label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {user.facebook || "Not provided"}
                    </h6>
                  </div>
                  <div className="mb-2">
                    <label className="form-label text-xxs mb-0">Linkedin</label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {user.linkedin || "Not provided"}
                    </h6>
                  </div>
                </div>
              </Card>
            </Col>

            {/* Account Information */}
            <Col md={6}>
              <Card className="custom-shadow">
                <h6 className="mt-3 px-3 fw-bold">Account Information</h6>
                <div className="px-3 mt-2">
                  <div className="mb-2">
                    <label className="form-label text-xxs mb-0 d-block">
                      Account Status
                    </label>
                    <Popover
                      styles={{ title: { fontFamily: "Inter" } }}
                      content={
                        <p
                          style={{
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            maxWidth: 300,
                            fontFamily: "Inter",
                          }}
                          className="text-xxs mb-1"
                        >
                          {getStatusNote(user.status, user.statusChangeNotes)}
                        </p>
                      }
                      title="Status Note"
                      placement="top"
                      className="custom-shadow"
                    >
                      <span
                        style={{
                          display: "inline-block",
                          cursor: user.statusNote ? "pointer" : "default",
                        }}
                      >
                        <SubtleBadge pill bg={badgeColor}>
                          {user.status || "Unknown"}
                        </SubtleBadge>
                      </span>
                    </Popover>

                    <Guard privilege={PRIV.UPDATE_USERS}>
                      <Button
                        size="sm"
                        variant="outline-success"
                        className="text-xs float-end"
                        onClick={() => setShowStatusModal(true)}
                      >
                        <FontAwesomeIcon icon="edit" />
                      </Button>
                    </Guard>
                  </div>

                  <div className="mb-2">
                    <label className="form-label text-xxs mb-0 d-block">
                      Identity Verification
                    </label>
                    <SubtleBadge
                      pill
                      bg={
                        user.userIdentityVerification?.verified
                          ? "success"
                          : "warning"
                      }
                    >
                      {user.userIdentityVerification?.verified
                        ? "Verified"
                        : "Pending"}
                    </SubtleBadge>
                  </div>

                  <div className="mb-3">
                    <label className="form-label text-xxs mb-0">
                      Work Authorization
                    </label>
                    <div>
                      <SubtleBadge
                        pill
                        bg={
                          !hasWorkAuthData
                            ? "secondary"
                            : user.userWorkAuthorization?.verified
                            ? "success"
                            : "warning"
                        }
                      >
                        {!hasWorkAuthData
                          ? "Not provided"
                          : user.userWorkAuthorization?.verified
                          ? "Verified"
                          : "Pending"}
                      </SubtleBadge>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label text-xxs mb-0">
                      Work Authorization Type
                    </label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {formatEnumValue(
                        workAuth.workAuthorizationType || workAuth.type,
                      )}
                    </h6>
                  </div>

                  <div className="mb-3">
                    <label className="form-label text-xxs mb-0">
                      Capability
                    </label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {formatEnumValue(workAuth.capability)}
                    </h6>
                  </div>

                  <div className="mb-3">
                    <label className="form-label text-xxs mb-0">Can Work</label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {formatBooleanValue(workAuth.canWork)}
                    </h6>
                  </div>

                  <div className="mb-3">
                    <label className="form-label text-xxs mb-0">
                      Requires Verification
                    </label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {formatBooleanValue(workAuth.requiresVerification)}
                    </h6>
                  </div>

                  <div className="mb-3">
                    <label className="form-label text-xxs mb-0">
                      Has Restrictions
                    </label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {formatBooleanValue(workAuth.hasRestrictions)}
                    </h6>
                  </div>

                  <div className="mb-3">
                    <label className="form-label text-xxs mb-0">
                      Work Authorization Expiration
                    </label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {workAuth.expirationDate
                        ? formatDateTime(workAuth.expirationDate)
                        : "Not provided"}
                    </h6>
                  </div>

                  <div className="mb-3">
                    <label className="form-label text-xxs mb-0">Work Auth Notes</label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {workAuth.notes || "Not provided"}
                    </h6>
                  </div>

                  <div className="mb-3">
                    <label className="form-label text-xxs mb-0">
                      ID Document Type
                    </label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {user.identificationDocumentType || "Not provided"}
                    </h6>
                  </div>

                  <div className="mb-3">
                    <label className="form-label text-xxs mb-0">
                      Created Date
                    </label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {formatDateTime(user.uploadedOn)}
                    </h6>
                  </div>

                  <div className="mb-3">
                    <label className="form-label text-xxs mb-0">
                      Last Updated
                    </label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {formatDateTime(user.securityPhotoAt)}
                    </h6>
                  </div>
                </div>
              </Card>
            </Col>

            {/* Address Information */}
            <Col md={6}>
              <Card className="custom-shadow">
                <h6 className="mt-3 px-3 fw-bold">Address Information</h6>
                <div className="px-3 mt-2">
                  <div className="mb-3">
                    <label className="form-label text-xxs mb-0">
                      Preferred Mailing Address
                    </label>
                    <h6 className="mb-0 fw-semibold text-xs">
                      {user.preferredMailingAddress || "Not provided"}
                    </h6>
                  </div>

                  <div className="mb-3">
                    <label className="form-label text-xxs mb-0">
                      Permanent Address
                    </label>
                    <h6 className="mb-0 fw-semibold text-xs">
                      {formatAddress(user.permanentAddress)}
                    </h6>
                  </div>

                  <div className="mb-3">
                    <label className="form-label text-xxs mb-0">
                      Residential Address
                    </label>
                    <h6 className="mb-0 fw-semibold text-xs">
                      {formatAddress(user.residentialAddress)}
                    </h6>
                  </div>

                  <div className="mb-3">
                    <label className="form-label text-xxs mb-0">
                      Work Address
                    </label>
                    <h6 className="mb-0 fw-semibold text-xs">
                      {formatAddress(user.workAddress)}
                    </h6>
                  </div>

                  <div className="mb-3">
                    <label className="form-label text-xxs mb-0">
                      Preferred Contact Method
                    </label>
                    <h6 className="mb-0 fw-semibold text-xs">
                      {user.preferredContactMethod || "Not specified"}
                    </h6>
                  </div>
                  <div className="mb-2">
                    <label className="form-label text-xxs mb-0">
                      Personal Email
                    </label>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {user.personalEmail || "Not provided"}
                    </h6>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </Col>

        <Col>
          <PaymentInfo userId={user.id} />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col>
          <UserAffiliationAssignmentsTable userId={user.id} />
        </Col>
      </Row>

      <Row className="mt-3">
        <Col>
          <UserAuditLogs userId={user.id} />
        </Col>
      </Row>

      <StatusUpdateModal
        show={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        user={user}
        loggedInUserId={loggedInUserId}
        onStatusUpdated={({ status, note }) =>
          setUser((prev) => ({ ...prev, status, statusChangeNotes: note }))
        }
      />
    </>
  );
};

export default UserDetailsPage;
